import { useState } from "react";
import { PhoneCall, CheckCircle2, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * „Rückruf anfordern" — ein Interessent hinterlässt mit einem Klick seine Nummer
 * zu einem konkreten Inserat. Der Händler erhält dieselbe Benachrichtigung wie
 * bei einem verpassten Anruf (Dashboard + E-Mail + SMS). Kein Twilio nötig.
 * Sichtbar nur, wenn der Händler die Verpasste-Anrufe-/Rückruf-Funktion (global
 * oder pro Inserat) aktiviert hat.
 */
export default function CallbackRequestButton({ listing, user }) {
    const owner = listing.owner || {};
    const effectiveEnabled = listing.missed_call_alerts_enabled != null
        ? listing.missed_call_alerts_enabled
        : !!owner.missed_call_alerts_enabled;
    const isOwner = !!(user && listing.owner_id === user.id);

    const [open, setOpen] = useState(false);
    const [done, setDone] = useState(false);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        name: (user && user !== false && user.name) || "",
        phone: (user && user !== false && user.phone) || "",
        note: "",
        preferred_time: "",
    });

    if (!effectiveEnabled || isOwner) return null;

    const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const submit = async () => {
        if (form.name.trim().length < 2 || form.phone.trim().length < 4) {
            toast.error("Bitte Name und Telefonnummer angeben.");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/listings/${listing.id}/callback-request`, {
                name: form.name.trim(),
                phone: form.phone.trim(),
                note: form.note.trim() || null,
                preferred_time: form.preferred_time.trim() || null,
            });
            setDone(true);
            setOpen(false);
            toast.success("Rückruf angefordert — der Verkäufer meldet sich bei Ihnen.");
        } catch {
            toast.error("Rückruf konnte nicht angefordert werden. Bitte erneut versuchen.");
        } finally {
            setBusy(false);
        }
    };

    if (done) {
        return (
            <div className="flex items-center gap-2 text-sm font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm p-2.5" data-testid="callback-requested-confirm">
                <CheckCircle2 className="w-4 h-4 shrink-0" />
                Rückruf angefordert — der Verkäufer meldet sich.
            </div>
        );
    }

    if (!open) {
        return (
            <button
                type="button"
                onClick={() => setOpen(true)}
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 border border-[#111827] text-[#111827] hover:bg-[#111827] hover:text-[#FFCC00] font-bold text-xs uppercase tracking-wider rounded-sm transition-colors"
                data-testid="callback-request-btn"
            >
                <PhoneCall className="w-4 h-4" /> Rückruf anfordern
            </button>
        );
    }

    return (
        <div className="border border-gray-200 rounded-sm p-3 bg-gray-50" data-testid="callback-request-form">
            <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-bold uppercase tracking-wider text-gray-600 flex items-center gap-1.5">
                    <PhoneCall className="w-3.5 h-3.5" /> Rückruf anfordern
                </div>
                <button type="button" onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-700" data-testid="callback-close-btn">
                    <X className="w-4 h-4" />
                </button>
            </div>
            <div className="space-y-2">
                <input
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    placeholder="Ihr Name"
                    className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#FFCC00] outline-none"
                    data-testid="callback-name-input"
                />
                <input
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    placeholder="Ihre Telefonnummer"
                    type="tel"
                    className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#FFCC00] outline-none"
                    data-testid="callback-phone-input"
                />
                <input
                    value={form.preferred_time}
                    onChange={(e) => set("preferred_time", e.target.value)}
                    placeholder="Wann erreichbar? (optional, z. B. 15–17 Uhr)"
                    className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#FFCC00] outline-none"
                    data-testid="callback-time-input"
                />
                <textarea
                    value={form.note}
                    onChange={(e) => set("note", e.target.value)}
                    placeholder="Nachricht (optional)"
                    rows={2}
                    className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#FFCC00] outline-none resize-none"
                    data-testid="callback-note-input"
                />
                <button
                    type="button"
                    onClick={submit}
                    disabled={busy}
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black text-xs uppercase tracking-wider rounded-sm disabled:opacity-60"
                    data-testid="callback-submit-btn"
                >
                    {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Wird gesendet…</> : <><PhoneCall className="w-4 h-4" /> Rückruf anfordern</>}
                </button>
            </div>
        </div>
    );
}
